import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { syncThemeColor } from './themeColor';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'dylo-v2-theme';

interface ThemeContextValue {
  /** Preferencia elegida por el usuario ('system' = seguir al SO). */
  preference: ThemePreference;
  /** Tema efectivo aplicado ('light' | 'dark'), ya resuelto. */
  resolvedTheme: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
  /** Alterna directo entre light y dark (ignora 'system'). */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const prefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const readStoredPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    /* localStorage no disponible */
  }
  return 'system';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    readStoredPreference
  );
  const [systemDark, setSystemDark] = useState<boolean>(prefersDark);

  // Escuchar cambios del tema del sistema
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme: ResolvedTheme =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  // Aplicar el atributo data-theme en <html>
  useEffect(() => {
    const root = document.documentElement;
    if (preference === 'system') {
      // Sin atributo: los tokens caen al @media (prefers-color-scheme)
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', preference);
    }
    return () => {
      // Al desmontar V2 (poco común), no dejamos el atributo colgado
      root.removeAttribute('data-theme');
    };
  }, [preference]);

  /** 🔄 21-oct-2026: la barra del navegador (Chrome en Android) sigue al fondo de la página. Ver `themeColor.ts`. */
  useEffect(() => syncThemeColor(), []);

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      /* noop */
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolvedTheme, setPreference, toggle }),
    [preference, resolvedTheme, setPreference, toggle]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};
